import { useMemo, useState } from "react";
import { Collapse, Typography } from "@mui/material";
import DynamicDataCardList from "../../hospital/components/DynamicDataCardList/DynamicDataCardList";
import TransferADTCardListTable from "./viewTransferStatusOfAdmission/transferADTCardListTable/TransferADTCardListTable";
import { ADT_TABLE_TYPE, CUSTOM_TAB_TYPE } from "../../../types/common.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import { PanelWrap } from "../../hospital/components/HospitalHeaderCardFilterPanel";

const CARDS_THAT_DONT_COMPARE_TO_DIFF_FACILITIES = [
	"Floor",
	"Doctor",
	"Per Nurse",
	"Hospitals resident transferred",
	"Per Facility",
];

/**
 * Header popover for ADT tab chips (`filterType === "ADT"`). Mirrors main ADTCard body (combine tab vs TransferADTCardListTable).
 */
export default function AdmissionHeaderAdtTabFilterPanel({
	adtHeaderRow,
	onClose: _onClose,
	cardFilter,
	cardFilterComparison,
	selectedADTTableData,
	selectedADTTableDataComparison,
	filterListData,
	filterListDataComparison,
	handleToggleADTTab,
	handleToggleAllADTTab,
	handleCombineADTCardClickForTab,
	dbSelectedFilters,
	dbSelectedFiltersComparison,
	filter,
	filterComparison,
	comparingAgainstDifferentFacility,
	rangesSet: _rangesSet,
	loading,
	loadingComparison,
	transferType,
	lockedTotalBy,
	filterTotal,
	projectionDays,
	percentageAgainst,
	dbData,
	dbDataComparison,
	lockFilterTotalState,
	lockFilterTotalStateComparison,
	reversedColorAdmission,
	getPriorityNumber,
	getADTPriorityNumber,
	headerFilterPopoverOpen,
}) {
	const [searchValue] = useState("");
	const [triggerSelectAll, setTriggerSelectAll] = useState(null);

	const tabItem = useMemo(
		() =>
			Array.isArray(selectedADTTableData)
				? selectedADTTableData.find(
						(x) => x.childId === adtHeaderRow?.childId && x.cardId === adtHeaderRow?.cardId
				  )
				: null,
		[selectedADTTableData, adtHeaderRow?.childId, adtHeaderRow?.cardId]
	);

	const tabItemComparison = useMemo(
		() =>
			Array.isArray(selectedADTTableDataComparison)
				? selectedADTTableDataComparison.find(
						(x) => x.childId === adtHeaderRow?.childId && x.cardId === adtHeaderRow?.cardId
				  )
				: null,
		[selectedADTTableDataComparison, adtHeaderRow?.childId, adtHeaderRow?.cardId]
	);

	const selectedSpecialArr = useMemo(
		() =>
			cardFilter?.adtAdmitPatientIds?.find(
				(x) => x.childId === adtHeaderRow?.childId && x.cardId === adtHeaderRow?.cardId
			),
		[cardFilter?.adtAdmitPatientIds, adtHeaderRow?.childId, adtHeaderRow?.cardId]
	);

	const dontCompareDiffFacilities = CARDS_THAT_DONT_COMPARE_TO_DIFF_FACILITIES.some((x) => x === tabItem?.subTitle);

	const isAdtCombineTab =
		tabItem?.type === ADT_TABLE_TYPE.CUSTOM && tabItem?.customTab?.type === CUSTOM_TAB_TYPE.combineTab;
	const combineAccessor = tabItem?.accessor;
	const tabDef = tabItem?.customTab;

	const combineData = useMemo(() => {
		if (!tabItem) return [];
		return Array.isArray(tabItem?.tableData) ? tabItem.tableData : filterListData?.[combineAccessor] ?? [];
	}, [tabItem, combineAccessor, filterListData]);

	const combineDataComparison = useMemo(() => {
		if (!tabItem) return [];
		return Array.isArray(tabItemComparison?.tableData)
			? tabItemComparison.tableData
			: filterListDataComparison?.[combineAccessor] ?? [];
	}, [tabItem, tabItemComparison, combineAccessor, filterListDataComparison]);

	const combineQuestionForList = tabDef != null ? { ...tabDef, type: CUSTOM_TAB_TYPE.combineTab } : null;
	const combinePageForList = tabItem?.parentId || PAGE_TYPE.ADMISSION;

	const adtSelectedIds = selectedSpecialArr?.selectedIds ?? [];
	const adtSelectedSpecialIds = selectedSpecialArr?.selectedSpecialIds ?? [];
	const isSelectedTotalCombine = adtSelectedIds.length > 0 || adtSelectedSpecialIds.length > 0;

	const handleCombineADTCardClick = async (_titleParam, uniqueKey) =>
		handleCombineADTCardClickForTab(tabItem, tabItemComparison, uniqueKey);

	const loadingBoth = loading || loadingComparison;

	const adtShellTitle = !tabItem
		? "ADT"
		: isAdtCombineTab
			? tabDef?.title || tabDef?.label || tabItem.subTitle || "Filter"
			: tabItem.subTitle || tabItem.title || "ADT";

	const inner = !tabItem ? (
		<Typography variant="body2" sx={{ p: 1, color: "text.secondary", fontSize: 12 }}>
			ADT tab data is not available.
		</Typography>
	) : isAdtCombineTab && combineAccessor && tabDef && combineQuestionForList ? (
				<DynamicDataCardList
					compactForHeaderPopover
					disableInteractions={false}
					totalTransfers={dbData?.total}
					totalTransfersComparison={dbDataComparison?.total}
					filterListData={{
						...filterListData,
						[combineAccessor]: combineData,
					}}
					filterListDataComparison={{
						...filterListDataComparison,
						[combineAccessor]: combineDataComparison,
					}}
					question={combineQuestionForList}
					cardTitle={tabDef?.title || tabDef?.label || tabItem.subTitle}
					data={combineData}
					dataComparison={combineDataComparison}
					handleToggle={handleToggleADTTab}
					handleToggleAll={handleToggleAllADTTab}
					selectedItem={adtSelectedIds}
					spacialSelectedItem={adtSelectedSpecialIds}
					type={combineAccessor}
					filter={filter}
					loading={loadingBoth}
					filterComparison={filterComparison}
					page={combinePageForList}
					averageCensus={percentageAgainst}
					projectionDays={projectionDays}
					isComparingAgainstAvgCensus={filterListData?.isComparingAgainstAvgCensus}
					priorityNumber={getPriorityNumber(combineAccessor)}
					transferType={transferType}
					lockedTotalBy={lockedTotalBy}
					isCustomTab={true}
					filterTotal={filterTotal}
					handleCardClick={handleCombineADTCardClick}
					isSelectedTotalCard={isSelectedTotalCombine}
					cardFilter={cardFilter}
					isDisabled={false}
					admissionCompareAgainst={
						lockFilterTotalState?.isLocked ? lockFilterTotalState?.lockedFilterTotal : dbData?.bedCapacity
					}
					admissionCompareAgainstComparison={
						lockFilterTotalStateComparison?.isLocked
							? lockFilterTotalStateComparison?.lockedFilterTotal
							: dbDataComparison?.bedCapacity
					}
					reverseColorsAdmissionPage={reversedColorAdmission}
				/>
			) : (
				<TransferADTCardListTable
					compactForHeaderPopover
					item={tabItem}
					itemComparison={tabItemComparison}
					dbSelectedFilters={dbSelectedFilters}
					filter={filter}
					filterComparison={filterComparison}
					dbSelectedFiltersComparison={dbSelectedFiltersComparison}
					handleToggleADTTab={handleToggleADTTab}
					handleToggleAll={handleToggleAllADTTab}
					filters={cardFilter.adtAdmitPatientIds}
					filtersComparison={cardFilterComparison?.adtAdmitPatientIds}
					type={"adtAdmitPatientIds"}
					page={PAGE_TYPE.ADMISSION}
					dbData={selectedADTTableData}
					dontCompareDiffFacilities={comparingAgainstDifferentFacility && dontCompareDiffFacilities}
					totalAdmissions={
						lockFilterTotalState?.isLocked ? lockFilterTotalState?.lockedFilterTotal : dbData?.bedCapacity
					}
					totalAdmissionsComparison={
						lockFilterTotalStateComparison?.isLocked
							? lockFilterTotalStateComparison?.lockedFilterTotal
							: dbDataComparison?.bedCapacity
					}
					projectionDays={projectionDays}
					isComparingAgainstAvgCensus={filterListData?.isComparingAgainstAvgCensus}
					priorityNumber={getADTPriorityNumber(tabItem)}
					transferType={transferType}
					lockedTotalBy={filterListData?.lockedTotalBy}
					specificData={tabItem}
					selectedSpecialIds={selectedSpecialArr?.selectedSpecialIds}
					selectedIds={selectedSpecialArr?.selectedIds}
					searchValue={searchValue}
					selectAll={false}
					triggerSelectAll={triggerSelectAll}
					setTriggerSelectAll={setTriggerSelectAll}
				/>
			);

	const body = (
		<PanelWrap title={adtShellTitle} contentSx={{ p: 0, pt: 0.5, overflow: "visible" }}>
			{inner}
		</PanelWrap>
	);

	if (headerFilterPopoverOpen === undefined) {
		return body;
	}

	return (
		<Collapse orientation="vertical" in={headerFilterPopoverOpen} timeout={1000} unmountOnExit={false}>
			{body}
		</Collapse>
	);
}
